import { useEffect, useRef, useState } from "react";
import { Page } from "../App";
// @ts-ignore
import * as THREE from "three";
import { cellToLatLng } from "h3-js";
import { fetchMobilityHex } from "../layers/fetchLayers";
import "./MobilityMarket.css";
import { cartoBasemapTileUrl } from "../config/carto";

const CENTER_LAT = 21.12;
const CENTER_LNG = 82.75;
const WORLD_TILE = 380;
const ZOOM = 5;

function project(lat: number, lng: number): [number, number] {
  const n = Math.pow(2, ZOOM);
  const tileX = ((lng + 180) / 360) * n;
  const tileY =
    ((1 -
      Math.log(
        Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)
      ) /
        Math.PI) /
      2) *
    n;
  const centerTileX = ((CENTER_LNG + 180) / 360) * n;
  const centerTileY =
    ((1 -
      Math.log(
        Math.tan((CENTER_LAT * Math.PI) / 180) +
          1 / Math.cos((CENTER_LAT * Math.PI) / 180)
      ) /
        Math.PI) /
      2) *
    n;
  return [
    (tileX - centerTileX) * WORLD_TILE,
    (tileY - centerTileY) * WORLD_TILE,
  ];
}

function latLngToTile(lat: number, lng: number, zoom: number) {
  const n = Math.pow(2, zoom);
  const x = Math.floor(((lng + 180) / 360) * n);
  const y = Math.floor(
    ((1 -
      Math.log(
        Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)
      ) /
        Math.PI) /
      2) *
      n
  );
  return { x, y };
}

function AnimatedStat({
  target,
  duration,
  active,
  decimals,
  suffix,
  prefix,
}: {
  target: number;
  duration: number;
  active: boolean;
  decimals: number;
  suffix: string;
  prefix?: string;
}) {
  const [count, setCount] = useState(0);
  const rafRef = useRef<number>();
  const startRef = useRef<number | undefined>();

  useEffect(() => {
    if (!active) {
      setCount(0);
      return;
    }
    startRef.current = undefined;
    const step = (ts: number) => {
      if (!startRef.current) startRef.current = ts;
      const progress = Math.min((ts - startRef.current) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const val = eased * target;
      setCount(decimals > 0 ? Math.round(val * 10) / 10 : Math.round(val));
      if (progress < 1) rafRef.current = requestAnimationFrame(step);
      else setCount(target);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [active, target, duration, decimals]);

  return (
    <>
      {prefix}
      {decimals > 0 ? count.toFixed(decimals) : count.toLocaleString()}
      {suffix}
    </>
  );
}

interface MobilityMarketProps {
  navigate: (p: Page) => void;
  onMapReady?: (instance: any) => void;
  selectedMetro?: { metro_origin: string; lat: number; lng: number } | null;
}

// ─── SEUILS PAR DÉFAUT (valeurs en nombre absolu) ───
const DEFAULT_THRESHOLDS = [
  { min: 0, max: 2000, label: "0 – 2k" },
  { min: 2000, max: 4000, label: "2 – 4k" },
  { min: 4000, max: 8000, label: "4 – 8k" },
  { min: 8000, max: 16000, label: "8 – 16k" },
  { min: 16000, max: 32000, label: "16 – 32k" },
  { min: 32000, max: 70000, label: "32 – 70k" },
  { min: 70000, max: Infinity, label: "Above 70k" },
];

const DEFAULT_COLORS = [
  "#003d33",
  "#005c4d",
  "#007a66",
  "#009983",
  "#33b39e",
  "#66ccb5",
  "#99e6d5",
];

export default function MobilityMarket({
  navigate,
  onMapReady,
  selectedMetro,
}: MobilityMarketProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const distRef = useRef(1500);
  const thetaRef = useRef(0);
  const phiRef = useRef(1.2);
  const targetRef = useRef({ x: -400, z: 0 });
  const updateCameraRef = useRef<() => void>(() => {});
  const hoveredMeshRef = useRef<any>(null);
  const hoveredOrigColorRef = useRef<any>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const markerRef = useRef<any>(null);
  const barsRef = useRef<any[]>([]);

  const [loaded, setLoaded] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hexCount, setHexCount] = useState(0);
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    weekly: number;
    total: number;
    city?: string;
    state?: string;
  } | null>(null);

  // ─── ÉTAT DE LA LÉGENDE ───
  const [legendData, setLegendData] = useState<
    { label: string; color: string; min: number; max: number }[]
  >(
    DEFAULT_THRESHOLDS.map((t, i) => ({
      label: t.label,
      color: DEFAULT_COLORS[i],
      min: t.min,
      max: t.max,
    }))
  );

  // ─── INTERPOLATION DE COULEUR ───
  const interpolateColor = (color1: string, color2: string, t: number) => {
    const c1 = new THREE.Color(color1);
    const c2 = new THREE.Color(color2);
    const c = c1.clone().lerp(c2, t);
    return c.getStyle();
  };

  // ─── EXPOSITION DES MÉTHODES POUR LA STEP 3 ───
  useEffect(() => {
    if (onMapReady) {
      onMapReady({
        zoomToMetro: (metro: { metro_origin: string; lat: number; lng: number }) => {
          const [x, z] = project(metro.lat, metro.lng);
          targetRef.current.x = x;
          targetRef.current.z = z;
          distRef.current = 400;
          updateCameraRef.current();
          addMetroMarker(metro);
        },
        resetView: () => {
          targetRef.current = { x: -400, z: 0 };
          distRef.current = 1500;
          thetaRef.current = 0;
          phiRef.current = 1.2;
          updateCameraRef.current();
          removeMarker();
        },
        getPosition: () => targetRef.current,
      });
    }
  }, [onMapReady]);

  // ─── MARQUEUR DE MÉTRO ───
  const addMetroMarker = (metro: { metro_origin: string; lat: number; lng: number }) => {
    removeMarker();
    const scene = sceneRef.current;
    if (!scene) return;

    const [x, z] = project(metro.lat, metro.lng);

    const outerRingGeo = new THREE.RingGeometry(10, 20, 48);
    const outerRingMat = new THREE.MeshBasicMaterial({
      color: 0x009a84,
      transparent: true,
      opacity: 0.8,
      side: THREE.DoubleSide,
    });
    const outerRing = new THREE.Mesh(outerRingGeo, outerRingMat);
    outerRing.position.set(x, 2, z);
    outerRing.rotation.x = -Math.PI / 2;
    scene.add(outerRing);

    const innerRingGeo = new THREE.RingGeometry(4, 10, 32);
    const innerRingMat = new THREE.MeshBasicMaterial({
      color: 0x009a84,
      transparent: true,
      opacity: 0.9,
      side: THREE.DoubleSide,
    });
    const innerRing = new THREE.Mesh(innerRingGeo, innerRingMat);
    innerRing.position.set(x, 2, z);
    innerRing.rotation.x = -Math.PI / 2;
    scene.add(innerRing);

    const dotGeo = new THREE.CircleGeometry(3, 16);
    const dotMat = new THREE.MeshBasicMaterial({
      color: 0x009a84,
      transparent: true,
      opacity: 1,
      side: THREE.DoubleSide,
    });
    const dot = new THREE.Mesh(dotGeo, dotMat);
    dot.position.set(x, 2.1, z);
    dot.rotation.x = -Math.PI / 2;
    scene.add(dot);

    const lineGeo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(x, 0, z),
      new THREE.Vector3(x, 8, z),
    ]);
    const lineMat = new THREE.LineBasicMaterial({
      color: 0x009a84,
      transparent: true,
      opacity: 0.4,
    });
    const line = new THREE.Line(lineGeo, lineMat);
    scene.add(line);

    const startTime = Date.now();
    const animateMarker = () => {
      if (!sceneRef.current) return;
      const elapsed = (Date.now() - startTime) / 1000;
      const pulse = 0.5 + 0.5 * Math.sin(elapsed * 2.5);
      outerRing.material.opacity = 0.3 + 0.5 * pulse;
      const scaleRing = 1 + 0.3 * Math.sin(elapsed * 2.5);
      outerRing.scale.set(scaleRing, scaleRing, scaleRing);
      dot.material.opacity = 0.6 + 0.4 * Math.sin(elapsed * 3);
      requestAnimationFrame(animateMarker);
    };
    animateMarker();

    markerRef.current = { outerRing, innerRing, dot, line };
  };

  const removeMarker = () => {
    const scene = sceneRef.current;
    if (!scene) return;
    if (markerRef.current) {
      const { outerRing, innerRing, dot, line } = markerRef.current;
      if (outerRing) scene.remove(outerRing);
      if (innerRing) scene.remove(innerRing);
      if (dot) scene.remove(dot);
      if (line) scene.remove(line);
      markerRef.current = null;
    }
  };

  // ─── CHARGEMENT DES DONNÉES AVEC LÉGENDE DYNAMIQUE (QUANTILES) ───
  const loadData = async (metroOrigin?: string) => {
    const scene = sceneRef.current;
    if (!scene) return;

    // Nettoyer les anciennes barres
    if (barsRef.current.length > 0) {
      barsRef.current.forEach((bar) => {
        scene.remove(bar.mesh);
        scene.remove(bar.edges);
      });
      barsRef.current = [];
    }

    setLoading(true);
    setStatsVisible(false);

    try {
      const rows = await fetchMobilityHex(metroOrigin);
      if (!rows || rows.length === 0) {
        setHexCount(0);
        const defaultLegend = DEFAULT_THRESHOLDS.map((t, i) => ({
          label: t.label,
          color: DEFAULT_COLORS[i],
          min: t.min,
          max: t.max,
        }));
        setLegendData(defaultLegend);
        setLoading(false);
        setStatsVisible(true);
        return;
      }

      // ── 1. Calcul de la nouvelle légende basée sur les quantiles ──
      let newLegendData: { label: string; color: string; min: number; max: number }[];

      if (metroOrigin) {
        // Extraire les valeurs weekly de chaque barre
        const weeklyValues = rows.map((r: any) => Number(r.trips_weekly) || 0);
        const sorted = [...weeklyValues].sort((a, b) => a - b);
        const n = sorted.length;

        // Nombre de groupes : au plus 7, mais on réduit si peu de valeurs
        const numGroups = Math.min(7, n);

        // Si une seule valeur ou toutes identiques
        if (numGroups === 1 || sorted[0] === sorted[n - 1]) {
          const val = Math.round(sorted[0]);
          const label = val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val.toString();
          newLegendData = [
            {
              label,
              color: DEFAULT_COLORS[3],
              min: sorted[0],
              max: sorted[n - 1],
            },
          ];
        } else {
          // Division en quantiles (effectifs équilibrés)
          const groupSize = n / numGroups;
          let startIdx = 0;
          newLegendData = [];
          for (let i = 0; i < numGroups; i++) {
            const endIdx = Math.min(Math.round((i + 1) * groupSize) - 1, n - 1);
            const minVal = sorted[startIdx];
            const maxVal = sorted[endIdx];
            const minRounded = Math.round(minVal);
            const maxRounded = Math.round(maxVal);
            let label: string;
            if (minRounded === maxRounded) {
              label = minRounded >= 1000 ? `${(minRounded / 1000).toFixed(0)}k` : minRounded.toString();
            } else {
              const minLabel = minRounded >= 1000 ? `${(minRounded / 1000).toFixed(0)}k` : minRounded.toString();
              const maxLabel = maxRounded >= 1000 ? `${(maxRounded / 1000).toFixed(0)}k` : maxRounded.toString();
              label = `${minLabel} – ${maxLabel}`;
            }
            const t = i / (numGroups - 1);
            const color = interpolateColor(DEFAULT_COLORS[0], DEFAULT_COLORS[6], t);
            newLegendData.push({
              label,
              color,
              min: minVal,
              max: maxVal,
            });
            startIdx = endIdx + 1;
          }
        }
      } else {
        // Pas de filtre → légende par défaut
        newLegendData = DEFAULT_THRESHOLDS.map((t, i) => ({
          label: t.label,
          color: DEFAULT_COLORS[i],
          min: t.min,
          max: t.max,
        }));
      }

      // ── 2. Mise à jour de l'état de la légende ──
      setLegendData(newLegendData);

      // ── 3. Construction des barres avec les couleurs de newLegendData ──
      const maxTotal = Math.max(...rows.map((r: any) => Number(r.trips_total) || 0));
      setHexCount(rows.length);

      const newBars: any[] = [];

      rows.forEach((r: any) => {
        const h3 = r.h3_destination;
        if (!h3) return;
        const weekly = Number(r.trips_weekly) || 0;
        const total = Number(r.trips_total) || 0;
        const tHeight = Math.sqrt(total / maxTotal);
        const h = Math.max(1.5, tHeight * 220);
        const [lat, lng] = cellToLatLng(h3);
        const [x, z] = project(lat, lng);

        let hexColor = newLegendData[newLegendData.length - 1]?.color || DEFAULT_COLORS[6];
        for (const item of newLegendData) {
          if (weekly >= item.min && weekly <= item.max) {
            hexColor = item.color;
            break;
          }
        }

        const color = new THREE.Color(hexColor);
        const geo = new THREE.CylinderGeometry(7, 7, h, 6, 1);
        const mat = new THREE.MeshBasicMaterial({
          color,
          transparent: true,
          opacity: 0.92,
        });
        const mesh = new THREE.Mesh(geo, mat);
        mesh.position.set(x, h / 2, z);
        scene.add(mesh);

        const edges = new THREE.LineSegments(
          new THREE.EdgesGeometry(geo),
          new THREE.LineBasicMaterial({
            color: 0x999999,
            transparent: true,
            opacity: 0.3,
          })
        );
        edges.position.set(x, h / 2, z);
        scene.add(edges);

        newBars.push({
          mesh,
          edges,
          weekly,
          total,
          city: r.destination_city || "",
          state: r.destination_state || "",
        });
      });

      barsRef.current = newBars;
      setLoading(false);
      setStatsVisible(true);
    } catch (error) {
      console.error("Error loading mobility data:", error);
      setLoading(false);
      setStatsVisible(true);
      const defaultLegend = DEFAULT_THRESHOLDS.map((t, i) => ({
        label: t.label,
        color: DEFAULT_COLORS[i],
        min: t.min,
        max: t.max,
      }));
      setLegendData(defaultLegend);
    }
  };

  // ─── RECHARGEMENT LORSQUE LE MÉTRO CHANGE ───
  useEffect(() => {
    const metroName = selectedMetro?.metro_origin;
    loadData(metroName);
  }, [selectedMetro]);

  // ─── INITIALISATION DE LA SCÈNE 3D ───
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const W = canvas.offsetWidth,
      H = canvas.offsetHeight;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 1);
    renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
    renderer.toneMapping = THREE.NoToneMapping;
    rendererRef.current = renderer;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(45, W / H, 1, 5000);
    cameraRef.current = camera;

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
    scene.add(ambientLight);

    const dirLight = new THREE.DirectionalLight(0xffffff, 0.7);
    dirLight.position.set(300, 600, 200);
    scene.add(dirLight);

    const dirLight2 = new THREE.DirectionalLight(0x004d40, 0.2);
    dirLight2.position.set(-400, 200, -300);
    scene.add(dirLight2);

    // ── TUILES CARTO ──
    const centerTile = latLngToTile(CENTER_LAT, CENTER_LNG, ZOOM);
    const TILES_RANGE = 10;
    const loader = new THREE.TextureLoader();
    const n = Math.pow(2, ZOOM);
    const refTileX = ((CENTER_LNG + 180) / 360) * n;
    const refTileY =
      ((1 -
        Math.log(
          Math.tan((CENTER_LAT * Math.PI) / 180) +
            1 / Math.cos((CENTER_LAT * Math.PI) / 180)
        ) /
          Math.PI) /
        2) *
      n;

    for (let dy = -TILES_RANGE; dy <= TILES_RANGE; dy++) {
      for (let dx = -TILES_RANGE; dx <= TILES_RANGE; dx++) {
        const tx = centerTile.x + dx;
        const ty = centerTile.y + dy;
        const tex = loader.load(cartoBasemapTileUrl("a", ZOOM, tx, ty));
        tex.colorSpace = THREE.LinearSRGBColorSpace;

        const worldX = (tx + 0.5 - refTileX) * WORLD_TILE;
        const worldZ = (ty + 0.5 - refTileY) * WORLD_TILE;
        const planeMesh = new THREE.Mesh(
          new THREE.PlaneGeometry(WORLD_TILE, WORLD_TILE),
          new THREE.MeshBasicMaterial({ map: tex })
        );
        planeMesh.rotation.x = -Math.PI / 2;
        planeMesh.position.set(worldX, -1, worldZ);
        scene.add(planeMesh);
      }
    }

    // ── MISE À JOUR DE LA CAMÉRA ──
    const updateCamera = () => {
      phiRef.current = Math.max(0.15, Math.min(1.5, phiRef.current));
      distRef.current = Math.max(120, Math.min(1800, distRef.current));
      const { x: tx, z: tz } = targetRef.current;
      camera.position.set(
        tx +
          distRef.current *
            Math.cos(phiRef.current) *
            Math.sin(thetaRef.current),
        distRef.current * Math.sin(phiRef.current),
        tz +
          distRef.current *
            Math.cos(phiRef.current) *
            Math.cos(thetaRef.current)
      );
      camera.lookAt(new THREE.Vector3(tx, 0, tz));
    };
    updateCameraRef.current = updateCamera;
    updateCamera();

    // ── CHARGEMENT INITIAL ──
    loadData();

    // ── INTERACTIONS SOURIS ──
    let dragMode: "none" | "orbit" | "pan" = "none";
    let px = 0,
      py = 0;
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    const onDown = (e: MouseEvent) => {
      dragMode = e.button === 2 || e.shiftKey ? "pan" : "orbit";
      px = e.clientX;
      py = e.clientY;
      canvas.style.cursor = "grabbing";
    };

    const onMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      if (dragMode === "none") {
        raycaster.setFromCamera(mouse, camera);
        const meshes = barsRef.current.map((b) => b.mesh);
        const hits = raycaster.intersectObjects(meshes);
        if (hits.length > 0) {
          const bar = barsRef.current.find((b) => b.mesh === hits[0].object);
          if (bar) {
            if (hoveredMeshRef.current && hoveredMeshRef.current !== bar.mesh) {
              hoveredMeshRef.current.material.color.set(
                hoveredOrigColorRef.current
              );
              hoveredMeshRef.current.material.opacity = 1;
              hoveredMeshRef.current.material.transparent = false;
            }
            if (hoveredMeshRef.current !== bar.mesh) {
              hoveredOrigColorRef.current = bar.mesh.material.color.getHex();
              hoveredMeshRef.current = bar.mesh;
              bar.mesh.material.color.set(0xeb280f);
              bar.mesh.material.transparent = true;
              bar.mesh.material.opacity = 0.75;
            }
            setTooltip({
              x: e.clientX,
              y: e.clientY,
              weekly: bar.weekly,
              total: bar.total,
              city: bar.city,
              state: bar.state,
            });
            canvas.style.cursor = "pointer";
          }
        } else {
          if (hoveredMeshRef.current) {
            hoveredMeshRef.current.material.color.set(
              hoveredOrigColorRef.current
            );
            hoveredMeshRef.current.material.opacity = 1;
            hoveredMeshRef.current.material.transparent = false;
            hoveredMeshRef.current = null;
          }
          setTooltip(null);
          canvas.style.cursor = "grab";
        }
        return;
      }

      const dx = e.clientX - px,
        dy = e.clientY - py;
      px = e.clientX;
      py = e.clientY;

      if (dragMode === "orbit") {
        thetaRef.current -= dx * 0.005;
        phiRef.current += dy * 0.005;
      } else {
        const panSpeed = distRef.current * 0.0012;
        targetRef.current.x += Math.cos(thetaRef.current) * dx * panSpeed;
        targetRef.current.z += dy * panSpeed;
        targetRef.current.x = Math.max(-800, Math.min(800, targetRef.current.x));
        targetRef.current.z = Math.max(-800, Math.min(800, targetRef.current.z));
      }
      updateCamera();
    };

    const onUp = () => {
      dragMode = "none";
      canvas.style.cursor = "grab";
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      distRef.current += e.deltaY * 0.6;
      updateCamera();
    };

    const onCtx = (e: Event) => e.preventDefault();

    canvas.addEventListener("mousedown", onDown);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    canvas.addEventListener("wheel", onWheel, { passive: false });
    canvas.addEventListener("contextmenu", onCtx);

    // ── TOUCHE ──
    let lastTouchDist = 0;
    const onTS = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        dragMode = "orbit";
        px = e.touches[0].clientX;
        py = e.touches[0].clientY;
      }
      if (e.touches.length === 2) {
        lastTouchDist = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
      }
    };
    const onTM = (e: TouchEvent) => {
      if (e.touches.length === 1 && dragMode === "orbit") {
        const dx = e.touches[0].clientX - px,
          dy = e.touches[0].clientY - py;
        px = e.touches[0].clientX;
        py = e.touches[0].clientY;
        thetaRef.current -= dx * 0.005;
        phiRef.current += dy * 0.005;
        updateCamera();
      }
      if (e.touches.length === 2) {
        const d = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
        distRef.current -= (d - lastTouchDist) * 1.5;
        lastTouchDist = d;
        updateCamera();
      }
    };
    canvas.addEventListener("touchstart", onTS);
    canvas.addEventListener("touchmove", onTM);
    canvas.addEventListener("touchend", onUp);

    // ── REDIMENSIONNEMENT ──
    const onResize = () => {
      const w = canvas.offsetWidth,
        h = canvas.offsetHeight;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    window.addEventListener("resize", onResize);

    // ── ANIMATION ──
    let animId: number;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      renderer.render(scene, camera);
    };
    animate();

    // ── NETTOYAGE ──
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      canvas.removeEventListener("mousedown", onDown);
      canvas.removeEventListener("wheel", onWheel);
      canvas.removeEventListener("contextmenu", onCtx);
      canvas.removeEventListener("touchstart", onTS);
      canvas.removeEventListener("touchmove", onTM);
      canvas.removeEventListener("touchend", onUp);
      renderer.dispose();
      sceneRef.current = null;
      cameraRef.current = null;
      rendererRef.current = null;
    };
  }, []);

  const fmtFull = (n: number) =>
    new Intl.NumberFormat("en-US").format(Math.round(n));

  return (
    <div className="mm">
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          cursor: "grab",
          touchAction: "none",
          zIndex: 1,
        }}
      />

      {tooltip && (
        <div
          style={{
            position: "fixed",
            left: tooltip.x + 14,
            top: tooltip.y + 14,
            zIndex: 2000,
            background: "rgba(6,6,8,0.96)",
            border: "1px solid rgba(0,153,131,0.35)",
            borderRadius: 12,
            padding: "12px 18px",
            pointerEvents: "none",
            fontFamily: "'ATR Innova',sans-serif",
            minWidth: 150,
            boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
          }}
        >
          {tooltip.city && (
            <div
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: "white",
                letterSpacing: -0.5,
                display: "flex",
                alignItems: "baseline",
                gap: 4,
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  color: "rgba(255,255,255,0.5)",
                  fontWeight: 400,
                  letterSpacing: 0.5,
                }}
              >
                City:
              </span>
              {tooltip.city}
            </div>
          )}
          {tooltip.state && (
            <div
              style={{
                fontSize: 11,
                color: "#009983",
                fontWeight: 600,
                letterSpacing: 0.5,
                marginTop: 2,
                display: "flex",
                alignItems: "baseline",
                gap: 4,
              }}
            >
              <span
                style={{
                  fontSize: 9,
                  color: "rgba(255,255,255,0.4)",
                  fontWeight: 400,
                  letterSpacing: 0.5,
                }}
              >
                State:
              </span>
              {tooltip.state}
            </div>
          )}
        </div>
      )}

      <nav className="mm-nav"></nav>

      <div className={`mm-content ${loaded ? "mm-content--in" : ""}`}>
        <div className="mm-left">
          <p className="mm-eyebrow">
            <span className="mm-eyebrow-dot" />
            Mobility Market · India
          </p>
          <h1 className="mm-title">
            Mobility
            <br />
            market in
            <br />
            <span className="mm-title-hl">India</span>
          </h1>

          {loading ? (
            <div className="mm-loading">
              <div className="mm-loading-dot" />
              <span>Loading 3D data...</span>
            </div>
          ) : (
            <>
              <div
                className="mm-stat-big"
                onMouseEnter={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.background = "rgba(0,153,131,0.14)";
                  el.style.borderColor = "rgba(0,153,131,0.4)";
                  el.style.transform = "translateX(4px)";
                  el.style.boxShadow =
                    "0 8px 32px rgba(0,153,131,0.2), inset 0 1px 0 rgba(255,255,255,0.05)";
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.background = "";
                  el.style.borderColor = "";
                  el.style.transform = "";
                  el.style.boxShadow = "";
                }}
                style={{ transition: "all 0.25s ease", cursor: "default" }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 6,
                  }}
                >
                  <span className="mm-stat-val">
                    <AnimatedStat
                      target={4.6}
                      duration={1800}
                      active={statsVisible}
                      decimals={1}
                      suffix="B"
                    />
                  </span>
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: "rgba(0,153,131,0.1)",
                      border: "1px solid rgba(0,153,131,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#009983"
                      strokeWidth="1.8"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M2 12h20M12 2a15 15 0 010 20M12 2a15 15 0 000 20" />
                    </svg>
                  </div>
                </div>
                <span className="mm-stat-label">
                  Total mobility market
                  <br />
                  All inter-city journeys · all modes
                </span>
                <div
                  style={{
                    marginTop: 10,
                    height: 2,
                    borderRadius: 2,
                    background:
                      "linear-gradient(to right, #009983, rgba(0,153,131,0.1))",
                  }}
                />
              </div>
              <div className="mm-stat-small">
                <span className="mm-stat-small-val">
                  <AnimatedStat
                    target={hexCount}
                    duration={1400}
                    active={statsVisible}
                    decimals={0}
                    suffix=""
                  />
                </span>
                <span className="mm-stat-small-label">
                  H3 hexagons analyzed
                </span>
              </div>
            </>
          )}

          <p className="mm-source">
            Source: ATR MobilityMonitor · CARTO BigQuery
          </p>
        </div>
      </div>

      {/* ── LÉGENDE — FOND DÉGRADÉ + OMBRE TURQUOISE (ATR) ── */}
{/* ── LÉGENDE : FOND TRANSPARENT + FLOU ── */}
<div
  style={{
    position: "absolute",
    bottom: "62px",
    right: "24px",
    zIndex: 100,
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    minWidth: "140px",
    padding: "12px 16px",
    borderRadius: "12px",
    background: "transparent",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    border: "none",
    boxShadow: "none",
  }}
>
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: "2px",
      marginBottom: "8px",
    }}
  >
    {/* Titre en blanc */}
    <div
      style={{
        fontSize: "11px",
        letterSpacing: "2px",
        textTransform: "uppercase",
        fontWeight: 700,
        color: "white",
      }}
    >
      Weekly trips · All modes
    </div>
    {/* Sous‑titre plus petit que le titre et moins bright */}
    {selectedMetro && (
      <div
        style={{
          fontSize: "12px",
          color: "rgba(255,255,255,0.6)",
          letterSpacing: "0.3px",
          fontWeight: 400,
          marginTop: "2px",
        }}
      >
        Metro area: {selectedMetro.metro_origin}
      </div>
    )}
  </div>

  {legendData.map((item) => (
    <div
      key={item.label}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        fontSize: "12px",
        color: "white",
        padding: "2px 0",
      }}
    >
      <div
        style={{
          width: "10px",
          height: "10px",
          borderRadius: "50%",
          background: item.color,
          flexShrink: 0,
        }}
      />
      <span>{item.label}</span>
    </div>
  ))}
</div>

      {/* ── CONTROLES DE ZOOM ── */}
      <div
        style={{
          position: "absolute",
          top: 72,
          right: 20,
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        <button
          onClick={() => {
            distRef.current = Math.max(120, distRef.current - 150);
            updateCameraRef.current();
          }}
          title="Zoom in"
          style={{
            width: 36,
            height: 36,
            background: "rgba(0,49,60,0.92)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 8,
            color: "rgba(255,255,255,0.7)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(12px)",
            transition: "all 0.15s",
            boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
          }}
          onMouseEnter={(e) => {
            const el = e.currentTarget as HTMLElement;
            el.style.background = "rgba(0,154,132,0.2)";
            el.style.color = "white";
            el.style.borderColor = "rgba(0,154,132,0.3)";
          }}
          onMouseLeave={(e) => {
            const el = e.currentTarget as HTMLElement;
            el.style.background = "rgba(0,49,60,0.92)";
            el.style.color = "rgba(255,255,255,0.7)";
            el.style.borderColor = "rgba(255,255,255,0.08)";
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
            <path d="M12 5v14M5 12h14" />
          </svg>
        </button>

        <button
          onClick={() => {
            distRef.current = Math.min(1800, distRef.current + 150);
            updateCameraRef.current();
          }}
          title="Zoom out"
          style={{
            width: 36,
            height: 36,
            background: "rgba(0,49,60,0.92)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 8,
            color: "rgba(255,255,255,0.7)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(12px)",
            transition: "all 0.15s",
            boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
          }}
          onMouseEnter={(e) => {
            const el = e.currentTarget as HTMLElement;
            el.style.background = "rgba(0,154,132,0.2)";
            el.style.color = "white";
            el.style.borderColor = "rgba(0,154,132,0.3)";
          }}
          onMouseLeave={(e) => {
            const el = e.currentTarget as HTMLElement;
            el.style.background = "rgba(0,49,60,0.92)";
            el.style.color = "rgba(255,255,255,0.7)";
            el.style.borderColor = "rgba(255,255,255,0.08)";
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
            <path d="M5 12h14" />
          </svg>
        </button>

        <button
          onClick={() => {
            distRef.current = 1500;
            thetaRef.current = 0;
            phiRef.current = 1.2;
            targetRef.current = { x: -400, z: 0 };
            updateCameraRef.current();
            removeMarker();
          }}
          title="Reset view"
          style={{
            width: 36,
            height: 36,
            background: "rgba(0,49,60,0.92)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 8,
            color: "rgba(255,255,255,0.7)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(12px)",
            transition: "all 0.15s",
            boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
          }}
          onMouseEnter={(e) => {
            const el = e.currentTarget as HTMLElement;
            el.style.background = "rgba(0,154,132,0.2)";
            el.style.color = "white";
            el.style.borderColor = "rgba(0,154,132,0.3)";
          }}
          onMouseLeave={(e) => {
            const el = e.currentTarget as HTMLElement;
            el.style.background = "rgba(0,49,60,0.92)";
            el.style.color = "rgba(255,255,255,0.7)";
            el.style.borderColor = "rgba(255,255,255,0.08)";
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <path d="M3 12a9 9 0 109-9 9.75 9.75 0 00-6.74 2.74L3 8" />
            <path d="M3 3v5h5" />
          </svg>
        </button>
      </div>
    </div>
  );
}